/**
 * CR-4865: Group POChangeLog entries by change_request_ref into the
 * Delivery-Appeal-style structure (submission, decision, history rows).
 */
import { useEffect, useState } from 'react';
import { ChangelogEntry } from '../common/Changelog';
import { POChangeRequestAttachment, purchasingAPI } from '../../lib/api';
import type { ChangeRequestGroup, ChangeRequestStatus } from './ChangeRequestCard';

// CR-7166: Added delivery_date mapping for customer-raised date changes.
// Customer changes their required date (delivery_date), supplier changes
// their promise date (supplier_promise_date).
const FIELD_LABEL: Record<string, string> = {
  supplier_promise_date: 'Promise Date',
  delivery_date: 'Required Date',
  quantity: 'Quantity',
  unit_price: 'Unit Price',
};

const FIELD_TO_CHANGE_TYPE: Record<string, ChangeRequestGroup['changeType']> = {
  supplier_promise_date: 'promise_date',
  delivery_date: 'required_date',
  quantity: 'quantity',
  unit_price: 'unit_price',
};

function deriveStatus(entry: ChangelogEntry | null): ChangeRequestStatus | null {
  if (!entry) return null;
  const reason = (entry.reason || '').toLowerCase();
  if (entry.field_changed === 'acknowledge_status') {
    if (entry.new_value === 'change_declined') return 'declined';
    if (entry.new_value === 'accepted') {
      if (/cancelled/.test(reason)) return 'cancelled';
      return 'accepted';
    }
    if (/cancelled/.test(reason)) return 'cancelled';
  }
  // Accept rows written on the field itself (supplier_promise_date etc.) with
  // "accepted" in the reason.
  if (/\b(accepted)\b/.test(reason)) return 'accepted';
  if (/\b(rejected|declined)\b/.test(reason)) return 'declined';
  if (/\bcancelled\b/.test(reason)) return 'cancelled';
  return null;
}

/**
 * Group changelog entries by `change_request_ref`. Only entries with a ref
 * participate — rows without a ref (legacy data) are returned in `orphans`
 * so the caller can render them via the legacy unordered list.
 */
export function groupChangeRequests(entries: ChangelogEntry[]): {
  groups: ChangeRequestGroup[];
  orphans: ChangelogEntry[];
} {
  const byRef = new Map<number, ChangelogEntry[]>();
  const orphans: ChangelogEntry[] = [];
  entries.forEach(entry => {
    const ref = entry.change_request_ref ?? null;
    if (ref == null) {
      orphans.push(entry);
      return;
    }
    const bucket = byRef.get(ref) || [];
    bucket.push(entry);
    byRef.set(ref, bucket);
  });

  const groups: ChangeRequestGroup[] = [];
  byRef.forEach((rows, ref) => {
    // Oldest-first ordering so the submission row (the proposal) is first
    // and any decision row comes later.
    const ordered = [...rows].sort(
      (a, b) => new Date(a.change_date).getTime() - new Date(b.change_date).getTime()
    );
    const submission = ordered.find(r => {
      const reason = (r.reason || '').toLowerCase();
      // The submission is the first row that proposes a value change. Skip
      // rows that look like decisions (accepted/declined/cancelled) — those
      // can also be on the field itself when written by accept_quantity_change /
      // accept_unit_price_change / round-1's accept_delivery_date.
      if (/\b(accepted|declined|rejected|cancelled)\b/.test(reason)) return false;
      // CR-7166: Also recognize delivery_date for customer-raised date changes.
      return (
        r.field_changed === 'supplier_promise_date' ||
        r.field_changed === 'delivery_date' ||
        r.field_changed === 'quantity' ||
        r.field_changed === 'unit_price'
      );
    }) || ordered[0];
    // CR-4731 round 2: decision rows are not always on acknowledge_status.
    // accept_quantity_change / accept_unit_price_change have always written
    // the decision on the field itself ('quantity' / 'unit_price'), and
    // CR-4731 round 1 made accept_delivery_date do the same on
    // 'supplier_promise_date'. Pick the latest row whose reason marks it as a
    // resolution; fall back to the legacy acknowledge_status row.
    const decision =
      [...ordered].reverse().find(r => {
        const reason = (r.reason || '').toLowerCase();
        return /\b(accepted|declined|rejected|cancelled)\b/.test(reason);
      }) ||
      ordered.find(r => r.field_changed === 'acknowledge_status') ||
      null;

    const derivedStatus = deriveStatus(decision);
    const status: ChangeRequestStatus = derivedStatus ?? 'open';

    const changeType: ChangeRequestGroup['changeType'] =
      FIELD_TO_CHANGE_TYPE[submission?.field_changed || ''] || 'other';
    const fieldLabel = FIELD_LABEL[submission?.field_changed || ''] || submission?.field_changed || 'Change';

    // Extract po_line id from any entry that carries it.
    const poLineId =
      (ordered.find(r => (r as unknown as { po_line?: number }).po_line)
        ?.[
          'po_line' as unknown as keyof ChangelogEntry
        ] as unknown as number) || 0;

    groups.push({
      ref,
      poLineId,
      status,
      changeType,
      fieldLabel,
      oldValue: submission?.old_value || '',
      newValue: submission?.new_value || '',
      submission: submission || null,
      decision,
      history: ordered,
    });
  });

  // Sort groups by ref DESC — newest requests at the top.
  groups.sort((a, b) => b.ref - a.ref);
  return { groups, orphans };
}

/**
 * Load all change-request attachments for a PO line and expose a
 * refresh handle. Indexed by change_request_ref for easy lookup per card.
 */
export function useChangeRequestAttachments(poLineId: number | null): {
  attachmentsByRef: Map<number, POChangeRequestAttachment[]>;
  loading: boolean;
  refresh: () => void;
} {
  const [attachments, setAttachments] = useState<POChangeRequestAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!poLineId) {
      setAttachments([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    purchasingAPI
      .getChangeRequestAttachments({ po_line: poLineId })
      .then(res => {
        if (cancelled) return;
        setAttachments(res.data.results || []);
      })
      .catch(() => {
        if (cancelled) return;
        setAttachments([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [poLineId, reloadTick]);

  const attachmentsByRef = new Map<number, POChangeRequestAttachment[]>();
  attachments.forEach(att => {
    const bucket = attachmentsByRef.get(att.change_request_ref) || [];
    bucket.push(att);
    attachmentsByRef.set(att.change_request_ref, bucket);
  });

  return {
    attachmentsByRef,
    loading,
    refresh: () => setReloadTick(t => t + 1),
  };
}
